import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useChat } from "../hooks/useChat";
import { useAppSelector } from "../store/hooks";
import { MessageCircle, Plus } from "lucide-react";
import { message } from "antd";
import FloatingChatMessage from "./FloatingChatMessage";
import FloatingChatInput from "./FloatingChatInput";
import FloatingModuleSelectionModal from "./FloatingModuleSelectionModal";

const FloatingChatContent = ({ triggerNewSession, onNewSessionTriggered }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const messagesEndRef = useRef(null);

  const {
    messages,
    isLoading,
    sendUserMessage,
    initializeSession,
    initializeDirectSession,
    currentModule,
    currentSessionId,
    streamingMessageId,
    modules,
    modulesLoading,
    fetchModules,
  } = useChat();

  const requireModuleSelection = useAppSelector(
    (state) => state.moduleSelection.requireModuleSelection
  );

  // Scroll to latest message when chat updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Fetch available modules once
  useEffect(() => {
    fetchModules().catch(() => message.error("Failed to load modules"));
  }, [fetchModules]);

  // Handle trigger from the Add Session button in header
  useEffect(() => {
    if (triggerNewSession && requireModuleSelection) {
      setIsModalVisible(true);
      if (onNewSessionTriggered) onNewSessionTriggered();
    }
  }, [triggerNewSession, requireModuleSelection, onNewSessionTriggered]);

  const handleSendMessage = (content) => {
    if (content?.trim()) sendUserMessage(content);
  };

  const handleCreateChat = (moduleId, moduleName) => {
    initializeSession(moduleId, moduleName);
    setIsModalVisible(false);
  };

  const handleStartSession = () => {
    initializeDirectSession();
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: 'inherit' }}>
      {/* ====== Chat Active ====== */}
      {currentSessionId ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex-1 flex flex-col overflow-hidden"
        >
          {/* Module Header */}
          {currentModule && (
            <div className="bg-gray-100 dark:bg-gray-800 shadow-sm px-4 py-3 border-b border-gray-300 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white text-center">
                {currentModule}
              </h3>
            </div>
          )}

          {/* Messages List */}
          <div
            className="flex-1 overflow-y-auto px-4 py-4 scrollbar-thin bg-white dark:bg-gray-900"
            id="floating-chat-messages"
          >
            {messages.map((msg) => (
              <FloatingChatMessage
                key={msg.id}
                message={msg}
                isStreaming={streamingMessageId === msg.id}
              />
            ))}

            {/* Loading Dots */}
            {isLoading && (
              <div className="flex justify-start mb-4">
                <div className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-2xl py-3 px-4 rounded-bl-md">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      {[0, 150, 300]?.map((delay) => (
                        <div
                          key={delay}
                          className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-400 animate-bounce"
                          style={{ animationDelay: `${delay}ms` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Field */}
          <FloatingChatInput onSendMessage={handleSendMessage} disabled={isLoading} />
        </motion.div>
      ) : (
        /* ====== Welcome Screen ====== */
        <div className="flex-1 flex items-center justify-center p-6 bg-white dark:bg-gray-900">
          <div className="text-center w-full mx-auto">
            {/* Header */}
            <div className="mb-6">
              <div className="w-16 h-16 bg-skin-button-base/10 dark:bg-skin-button-base/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-skin-button-base" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                AI Assistant
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed px-2">
                Start a conversation with our specialized modules and get quick, reliable answers.
              </p>
            </div>

            {/* Action Button */}
            <div className="mb-6">
              {requireModuleSelection ? (
                <button
                  onClick={() => setIsModalVisible(true)}
                  className="inline-flex items-center justify-center gap-2 font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 bg-skin-button-base text-white hover:bg-skin-button-hover focus:ring-2 focus:ring-skin-base focus:ring-offset-2 w-full"
                >
                  <Plus size={16} />
                  Start New Chat
                </button>
              ) : (
                <button
                  onClick={handleStartSession}
                  className="inline-flex items-center justify-center gap-2 font-medium py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 bg-skin-button-base text-white hover:bg-skin-button-hover focus:ring-2 focus:ring-skin-base focus:ring-offset-2 w-full"
                >
                  <MessageCircle size={16} />
                  Start Session
                </button>
              )}
            </div>

            {/* Available Modules Preview */}
            {requireModuleSelection && (
              <div className="space-y-3 w-full">
                {modulesLoading ? (
                  <div className="flex justify-center items-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-skin-button-base"></div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide text-center">
                      Available Modules
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {modules.slice(0, 3).map((mod) => (
                        <motion.button
                          key={mod.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleCreateChat(mod.id, mod.name)}
                          className="w-full p-3 text-left rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 text-sm"
                        >
                          <div className="font-medium mb-1 text-gray-900 dark:text-white truncate">
                            {mod.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                            {mod.desc}
                          </div>
                        </motion.button>
                      ))}
                    </div>
                    {modules.length > 3 && (
                      <button
                        onClick={() => setIsModalVisible(true)}
                        className="text-xs text-skin-button-base hover:underline w-full text-center py-2"
                      >
                        View all {modules.length} modules
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ====== Module Selection Modal ====== */}
      {requireModuleSelection && (
        <FloatingModuleSelectionModal
          isOpen={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          onStartChat={handleCreateChat}
        />
      )}
    </div>
  );
};

export default FloatingChatContent;
